/**
 * ConfirmDialog — promise-based confirm modal built on shadcn AlertDialog.
 *
 * Replaces `window.confirm()` for destructive admin actions so the pop-up
 * matches the app's branding, is keyboard-accessible, and is harder to
 * click through by accident (two-step commit + explicit "Delete" button).
 *
 * Usage:
 *   1. Wrap the app once in <ConfirmDialogProvider>...
 *   2. In any component:
 *        const confirm = useConfirm();
 *        if (!(await confirm({
 *          title: 'Delete referral?',
 *          description: 'This will remove the record and all attached files.',
 *          confirmLabel: 'Delete',
 *          destructive: true,
 *        }))) return;
 *
 * The `destructive: true` flag paints the confirm button red (bg-red-600).
 */
import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './alert-dialog';

const ConfirmContext = createContext(null);

const INITIAL = {
  open: false,
  title: 'Are you sure?',
  description: '',
  confirmLabel: 'Confirm',
  cancelLabel: 'Cancel',
  destructive: false,
};

export function ConfirmDialogProvider({ children }) {
  const [state, setState] = useState(INITIAL);
  const resolveRef = useRef(null);

  const confirm = useCallback((options = {}) => {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setState({
        open: true,
        title: options.title || INITIAL.title,
        description: options.description || '',
        confirmLabel: options.confirmLabel || (options.destructive ? 'Delete' : 'Confirm'),
        cancelLabel: options.cancelLabel || 'Cancel',
        destructive: !!options.destructive,
      });
    });
  }, []);

  const handleClose = useCallback((result) => {
    setState((s) => ({ ...s, open: false }));
    // Resolve after the close animation kicks off so the promise fires
    // deterministically for callers that await it.
    if (resolveRef.current) {
      const r = resolveRef.current;
      resolveRef.current = null;
      r(result);
    }
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AlertDialog
        open={state.open}
        onOpenChange={(open) => {
          // Radix fires this on Escape / overlay click too. Treat any close
          // that's not an explicit action button click as "Cancel".
          if (!open) handleClose(false);
        }}
      >
        <AlertDialogContent data-testid="confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle data-testid="confirm-dialog-title">{state.title}</AlertDialogTitle>
            {state.description ? (
              <AlertDialogDescription data-testid="confirm-dialog-description">
                {state.description}
              </AlertDialogDescription>
            ) : null}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => handleClose(false)}
              data-testid="confirm-dialog-cancel"
            >
              {state.cancelLabel}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleClose(true)}
              className={state.destructive ? 'bg-red-600 hover:bg-red-700 text-white' : ''}
              data-testid="confirm-dialog-confirm"
            >
              {state.confirmLabel}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error('useConfirm must be used within <ConfirmDialogProvider>');
  }
  return ctx;
}
